/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import DataRepo from '@api/datasource/data'
import QueryKeys from '@constants/queryKeys'
import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Group,
  HoverCard,
  LoadingOverlay,
  Paper,
  ScrollArea,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useListState, useLocalStorage } from '@mantine/hooks'
import Input from '@components/shared/input'
import lodash from 'lodash'
import TableCustom, { GenericColumnType } from '@components/shared/table'
import {
  EventBaseType,
  EventTypeType,
  SearchEventType,
  StatusEventType,
  UpdateEventType,
} from '@customTypes/event'
import {
  IconEdit,
  IconPaperclip,
  IconPhoneCall,
  IconPlus,
  IconTrash,
  IconUser,
} from '@tabler/icons-react'
import moment from 'moment'
import { DATE_FORMAT } from '@constants/app'
import { ModalFormType } from '@customTypes/page'
import EventForm, { EventFormType } from '@components/event/form'
import { ErrorService } from '@utils/error'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import ImagePreview from '@components/shared/imagePreview'
import ConfirmationModal from '@components/shared/confirmationModal'
import {
  debounceUpdate,
  isLoadingMutation,
  isLoadingOrRefetchQuery,
  isLoadingQuery,
} from '@utils/network'
import { onFilterEvents } from '@utils/client'
import { useIsMobile, useTableMinWidth } from '@hooks/mobile'
import { transformColor } from '@utils/theme'
import { ReactSortable } from 'react-sortablejs'

import 'react-big-calendar/lib/css/react-big-calendar.css'
import './calendar.scss'

import TextIcon from '@components/shared/textIcon'
import EventPreview from '@components/event/preview'

const ViewsOptions = {
  table: {
    label: 'Tabla',
    value: 'table',
  },
  boardByStatus: {
    label: 'Eventos por Estado',
    value: 'boardByStatus',
  },
  boardByType: {
    label: 'Eventos por Tipo',
    value: 'boardByType',
  },
  calendar: {
    label: 'Calendario',
    value: 'calendar',
  },
}

type ViewsOptionsType = 'table' | 'boardByStatus' | 'boardByType' | 'calendar'

type GroupEventByStatus = StatusEventType & {
  events: EventBaseType[]
}

type GroupEventByType = EventTypeType & {
  events: EventBaseType[]
}

const localizer = momentLocalizer(moment) // or globalizeLocalizer

const HomeUser = () => {
  const [viewSelected, setViewSelected] = useLocalStorage<ViewsOptionsType>({
    defaultValue: ViewsOptions.table.value as 'table',
    key: 'viewSelected',
    sync: true,
  })

  const isMobile = useIsMobile()

  const minWidth = useTableMinWidth(isMobile)

  const [selectedRows, handlers] = useListState<string>([])

  const [params, setParams] = React.useState<SearchEventType>({})

  const [groupedDataByStatus, setGroupedDataByStatus] = React.useState<GroupEventByStatus[]>([])

  const [groupedDataByType, setGroupedDataByType] = React.useState<GroupEventByType[]>([])

  const [modalImagePreview, setModalImagePreview] = React.useState<ModalFormType<string[]>>({
    open: false,
  })

  const [modalEventForm, setEventForm] = React.useState<ModalFormType<EventFormType>>({
    open: false,
  })

  const [modalEventPreview, setEventPreview] = React.useState<ModalFormType<EventFormType>>({
    open: false,
  })

  const [eventModalDelete, setEventModalDelete] = React.useState<EventBaseType[] | null>(null)

  const eventTypesQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventTypeOptions(),
  })

  const eventStatusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventStatusOptions(),
  })

  const eventsAllQuery = useQuery({
    queryKey: [QueryKeys.GET_EVENTS_ALL],
    placeholderData: [],
    queryFn: async () => {
      const response = await DataRepo.clientEntityService.getEvents({})
      return response
    },
  })

  const calendarParse = useQuery({
    queryKey: ['parse-events', eventsAllQuery.data],
    placeholderData: [],
    queryFn: async () => {
      return (
        eventsAllQuery.data?.map((event) => ({
          title: event.name,
          start: moment.unix(event.date).toDate(),
          end: moment.unix(event.endDate).toDate(),
          allDay: false,
          resource: event,
        })) || []
      )
    },
  })

  const deleteEventsMutation = useMutation<void, ErrorService, string[]>({
    mutationFn: async (uids) => {
      const response = await DataRepo.clientEntityService.deleteEvents({
        uids,
      })

      await queryClient.invalidateQueries({
        predicate: (query) =>
          [QueryKeys.GET_EVENTS_KEY, QueryKeys.GET_EVENTS_ALL].includes(
            query.queryKey[0] as string,
          ),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar el evento',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Eliminación exitosa',
      })

      setEventModalDelete(null)
    },
  })

  const eventPropertyMutation = useMutation<void, Error, UpdateEventType>({
    mutationFn: (data) => {
      return DataRepo.clientEntityService.updateEvent(data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENTS_KEY],
        refetchType: 'all',
      })
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENTS_ALL],
        refetchType: 'all',
      })
    },
    onError: (error) => {
      notifications.show({
        title: 'Error al actualizar el evento',
        message: error.message || 'Ocurrió un error al actualizar el evento',
        color: 'red',
      })
    },
  })

  const groupedEventsByStatusFromQuery = React.useMemo(() => {
    if (!eventsAllQuery.data || !eventStatusQuery.data?.length) return []

    const objectGroup = lodash.groupBy(
      eventsAllQuery.data,
      (event) => event.status?.uid || 'unknown',
    )

    const arrayGroups: GroupEventByStatus[] = eventStatusQuery.data.map((status) => ({
      uid: status.uid,
      name: status.name,
      color: status.color,
      order: status.order,
      createdAt: status.createdAt,
      updatedAt: status.updatedAt,
      events: objectGroup[status.uid] || [],
    }))

    arrayGroups.push({
      uid: 'unknown',
      name: 'Sin estado',
      color: '#ccc',
      order: 9999,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      events: objectGroup['unknown'] || [],
    })

    return arrayGroups.sort((a, b) => {
      if (a.name === 'Sin estado') return 1
      if (b.name === 'Sin estado') return -1
      return a.order - b.order
    })
  }, [eventsAllQuery.data, eventStatusQuery.data])

  const groupedEventsByTypeFromQuery = React.useMemo(() => {
    if (!eventsAllQuery.data || !eventTypesQuery.data?.length) return []

    const objectGroup = lodash.groupBy(eventsAllQuery.data, (event) => event.type?.uid || 'unknown')

    const arrayGroups: GroupEventByType[] = eventTypesQuery.data.map((type) => ({
      uid: type.uid,
      name: type.name,
      color: type.color,
      createdAt: type.createdAt,
      updatedAt: type.updatedAt,
      events: objectGroup[type.uid] || [],
    }))

    arrayGroups.push({
      uid: 'unknown',
      name: 'Sin tipo',
      color: '#ccc',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      events: objectGroup['unknown'] || [],
    })

    return arrayGroups.sort((a, b) => {
      if (a.name === 'Sin tipo') return 1
      if (b.name === 'Sin tipo') return -1
      return a.name.localeCompare(b.name)
    })
  }, [eventsAllQuery.data, eventTypesQuery.data])

  React.useEffect(() => {
    setGroupedDataByStatus(groupedEventsByStatusFromQuery)
  }, [groupedEventsByStatusFromQuery])

  React.useEffect(() => {
    setGroupedDataByType(groupedEventsByTypeFromQuery)
  }, [groupedEventsByTypeFromQuery])

  const columns = buildColumns()

  const memoFilterClient = React.useMemo(
    () =>
      onFilterEvents({
        data: eventsAllQuery.data,
        params,
      }),
    [eventsAllQuery.data, params],
  )

  const isLoadingAllEvents = isLoadingQuery(eventsAllQuery)

  const isLoadingParsing = isLoadingOrRefetchQuery(calendarParse)

  return (
    <div className="cd-flex cd-flex-col cd-gap-y-[1rem] cd-w-full cd-h-full cd-grow">
      <Input
        data={Object.values(ViewsOptions)}
        typeInput="segmentedControl"
        value={viewSelected}
        onChange={(value) => setViewSelected(value as ViewsOptionsType)}
      />

      {viewSelected === ViewsOptions.table.value && (
        <React.Fragment>
          <TableCustom
            allData={eventsAllQuery.data || []}
            columns={columns}
            data={memoFilterClient}
            keyId="uid"
            loading={isLoadingAllEvents}
            loadingMessage="Cargando eventos"
            minWidth={minWidth}
            selectedRows={selectedRows}
            onChangeSelected={handlers}
            onDelete={(ids) => {
              const newEvents = eventsAllQuery.data?.filter((item) => ids.includes(item.uid))

              if (!newEvents?.length) return

              setEventModalDelete(newEvents)
            }}
            onSearch={(query, field) => setParams({ query, field })}
          />
        </React.Fragment>
      )}

      {viewSelected === ViewsOptions.boardByStatus.value && (
        <ScrollArea.Autosize className="cd-h-full cd-grow">
          <LoadingOverlay
            loaderProps={{ type: 'dots' }}
            overlayProps={{ radius: 'md', blur: 2 }}
            visible={isLoadingAllEvents}
            zIndex={1000}
          />
          <div className="cd-mb-4 cd-flex cd-flex-row cd-gap-4">
            {groupedDataByStatus.map((group, idx) => (
              <Paper
                bg={transformColor({
                  color: group.color,
                  shift: 0.25,
                  alpha: 0.2,
                })}
                className="!cd-p-2 cd-rounded-lg cd-grow cd-w-[300px]"
                key={`group-${idx}-${group.uid}`}
              >
                <Box className="cd-flex cd-items-center cd-justify-between cd-rounded-tl-lg cd-rounded-tr-lg">
                  <Title
                    bg={group.color}
                    className="!cd-text-lg !cd-font-semibold !cd-text-white cd-px-3 cd-rounded-md"
                  >
                    {group.name}
                  </Title>
                  <div className="cd-flex cd-items-center cd-justify-end cd-gap-2">
                    <ActionIcon
                      color={transformColor({
                        color: group.color,
                        shift: -0.1,
                      })}
                      variant="transparent"
                      onClick={() =>
                        setEventForm({ open: true, data: { initialStatus: group.uid } })
                      }
                    >
                      <IconPlus />
                    </ActionIcon>
                  </div>
                </Box>
                <ScrollArea.Autosize className="cd-mt-2" mah={600}>
                  <ReactSortable
                    animation={200}
                    className="cd-flex cd-flex-col cd-gap-2 cd-min-h-[150px] cd-w-full"
                    delay={2}
                    group={{ name: 'shared', pull: true, put: true }}
                    id={group.uid}
                    list={group.events.map((client) => ({
                      id: client.uid,
                      name: client.name,
                    }))}
                    setList={() => null}
                    onAdd={handleAddStatus}
                  >
                    {group.events.map((event) => (
                      <Paper
                        withBorder
                        className="cd-py-2 cd-px-4 cd-rounded-md cd-cursor-pointer"
                        data-id={event.uid}
                        key={event.uid}
                        onClick={(e) => {
                          e.stopPropagation()
                          // setClientPreview({ open: true, data: event })
                          setEventForm({
                            open: true,
                            data: {
                              clientID: event.client?.uid || '',
                              event,
                            },
                          })
                        }}
                      >
                        <div className="cd-flex cd-flex-col cd-gap-6">
                          <div className="cd-flex cd-flex-col cd-gap-2">
                            <Text>{event.name}</Text>
                            {/* <Badge color={event.status?.color || 'gray'} size="md">
                              {event.status?.name || 'Sin estado'}
                            </Badge> */}
                            <Badge color={event.type?.color || 'gray'} size="md">
                              {event.type?.name || 'Sin tipo'}
                            </Badge>
                          </div>

                          <div className="cd-flex cd-flex-row cd-items-center cd-justify-start cd-gap-4">
                            <ActionIcon
                              color="gray"
                              size="md"
                              variant="light"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEventForm({
                                  open: true,
                                  data: {
                                    clientID: event.client?.uid || '',
                                    event,
                                  },
                                })
                              }}
                            >
                              <IconEdit size={20} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEventModalDelete([event])
                              }}
                            >
                              <IconTrash size={20} />
                            </ActionIcon>
                          </div>
                        </div>
                      </Paper>
                    ))}
                  </ReactSortable>
                </ScrollArea.Autosize>
              </Paper>
            ))}
          </div>
        </ScrollArea.Autosize>
      )}

      {viewSelected === ViewsOptions.boardByType.value && (
        <ScrollArea.Autosize className="cd-h-full cd-grow">
          <LoadingOverlay
            loaderProps={{ type: 'dots' }}
            overlayProps={{ radius: 'md', blur: 2 }}
            visible={isLoadingAllEvents}
            zIndex={1000}
          />
          <div className="cd-mb-4 cd-flex cd-flex-row cd-gap-4">
            {groupedDataByType.map((group, idx) => (
              <Paper
                bg={transformColor({
                  color: group.color,
                  shift: 0.25,
                  alpha: 0.2,
                })}
                className="!cd-p-2 cd-rounded-lg cd-grow cd-w-[300px]"
                key={`group-${idx}-${group.uid}`}
              >
                <Box className="cd-flex cd-items-center cd-justify-between cd-rounded-tl-lg cd-rounded-tr-lg">
                  <Title
                    bg={group.color}
                    className="!cd-text-lg !cd-font-semibold !cd-text-white cd-px-3 cd-rounded-md"
                  >
                    {group.name}
                  </Title>
                  <div className="cd-flex cd-items-center cd-justify-end cd-gap-2">
                    <ActionIcon
                      color={transformColor({
                        color: group.color,
                        shift: -0.1,
                      })}
                      variant="transparent"
                      onClick={() => setEventForm({ open: true, data: { initialType: group.uid } })}
                    >
                      <IconPlus />
                    </ActionIcon>
                  </div>
                </Box>
                <ScrollArea.Autosize className="cd-mt-2" mah={600}>
                  <ReactSortable
                    animation={200}
                    className="cd-flex cd-flex-col cd-gap-2 cd-min-h-[150px] cd-w-full"
                    delay={2}
                    group={{ name: 'shared', pull: true, put: true }}
                    id={group.uid}
                    list={group.events.map((client) => ({
                      id: client.uid,
                      name: client.name,
                    }))}
                    setList={() => null}
                    onAdd={handleAddType}
                  >
                    {group.events.map((event) => (
                      <Paper
                        withBorder
                        className="cd-py-2 cd-px-4 cd-rounded-md cd-cursor-pointer"
                        data-id={event.uid}
                        key={event.uid}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEventForm({
                            open: true,
                            data: {
                              clientID: event.client?.uid || '',
                              event,
                            },
                          })
                        }}
                      >
                        <div className="cd-flex cd-flex-col cd-gap-6">
                          <div className="cd-flex cd-flex-col cd-gap-2">
                            <Text>{event.name}</Text>
                            <Badge color={event.status?.color || 'gray'} size="md">
                              {event.status?.name || 'Sin estado'}
                            </Badge>
                          </div>

                          <div className="cd-flex cd-flex-row cd-items-center cd-justify-start cd-gap-4">
                            <ActionIcon
                              color="gray"
                              size="md"
                              variant="light"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEventForm({
                                  open: true,
                                  data: {
                                    clientID: event.client?.uid || '',
                                    event,
                                  },
                                })
                              }}
                            >
                              <IconEdit size={20} />
                            </ActionIcon>
                            <ActionIcon
                              color="red"
                              variant="light"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEventModalDelete([event])
                              }}
                            >
                              <IconTrash size={20} />
                            </ActionIcon>
                          </div>
                        </div>
                      </Paper>
                    ))}
                  </ReactSortable>
                </ScrollArea.Autosize>
              </Paper>
            ))}
          </div>
        </ScrollArea.Autosize>
      )}

      {viewSelected === ViewsOptions.calendar.value && (
        <React.Fragment>
          <LoadingOverlay
            loaderProps={{ type: 'dots' }}
            overlayProps={{ radius: 'md', blur: 2 }}
            visible={isLoadingAllEvents || isLoadingParsing}
            zIndex={1000}
          />

          <Calendar
            className="cd-h-full cd-w-full cd-flex cd-grow"
            components={{
              event: ({ event }) => (
                <Paper
                  className="!cd-bg-white dark:!cd-bg-zinc-300"
                  shadow="md"
                  onClick={(e) => {
                    e.stopPropagation()
                    setEventPreview({ open: true, data: { event: event.resource } })
                  }}
                >
                  <Box
                    bg={transformColor({
                      color: event.resource.type?.color || '#ccc',
                      shift: 0.75,
                      alpha: 1,
                    })}
                    className="cd-p-2 cd-cursor-pointer"
                    style={{
                      borderWidth: 1,
                      borderRadius: 5,
                      borderColor: event.resource.type?.color || 'gray',
                    }}
                  >
                    <div className="cd-flex cd-flex-row cd-items-start cd-justify-between cd-gap-2">
                      <div className="cd-flex cd-flex-col cd-items-start cd-justify-start cd-gap-2">
                        <Text className="cd-text-sm cd-font-semibold cd-text-gray-800 dark:cd-text-gray-200">
                          {event.title}
                        </Text>
                        {event.resource.description?.length && (
                          <Text className="cd-text-xs cd-text-gray-500 dark:cd-text-gray-400">
                            {event.resource.description}
                          </Text>
                        )}
                      </div>

                      <div className="cd-flex cd-flex-row cd-items-center cd-justify-end cd-gap-2">
                        <Badge color={event.resource.type?.color || 'gray'}>
                          {event.resource.type?.name || 'Sin tipo'}
                        </Badge>
                        <Badge color={event.resource.status?.color || 'gray'}>
                          {event.resource.status?.name || 'Sin estado'}
                        </Badge>
                      </div>
                    </div>

                    <Divider
                      className="cd-my-2"
                      color={transformColor({
                        color: event.resource.type?.color || '#ccc',
                        shift: 0.25,
                        alpha: 0.75,
                      })}
                    />
                    <TextIcon
                      className="cd-text-xs cd-gap-[0.25rem]"
                      leftIcon={<IconUser size={14} />}
                    >
                      {event.resource.client?.name || 'Sin cliente'}
                    </TextIcon>
                    <TextIcon
                      className="cd-text-xs cd-gap-[0.25rem]"
                      leftIcon={<IconPhoneCall size={14} />}
                    >
                      {event.resource.contacts?.length
                        ? `${event.resource.contacts.length} contacto(s)`
                        : 'Sin contacto'}
                    </TextIcon>
                  </Box>
                </Paper>
              ),
              eventWrapper: ({ event }) => (
                <HoverCard withArrow shadow="md" styles={{ dropdown: { maxWidth: 400 } }}>
                  <HoverCard.Target>
                    <Paper className="!cd-bg-white dark:!cd-bg-zinc-300" shadow="md">
                      <Box
                        bg={transformColor({
                          color: event.resource.type?.color || '#ccc',
                          shift: 0.75,
                          alpha: 1,
                        })}
                        className="cd-p-1 cd-cursor-pointer"
                        style={{
                          borderWidth: 1,
                          borderRadius: 5,
                          borderColor: event.resource.type?.color || 'gray',
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEventPreview({ open: true, data: { event: event.resource } })
                        }}
                      >
                        <div className="cd-flex cd-flex-row cd-gap-2 cd-items-center">
                          <Text className="cd-text-xs">{moment(event.start).format('HH:mm')}</Text>
                          <Text className="cd-text-sm cd-text-gray-800 dark:cd-text-gray-200">
                            {event.title}
                          </Text>
                        </div>
                      </Box>
                    </Paper>
                  </HoverCard.Target>
                  <HoverCard.Dropdown>
                    <Box className="cd-flex cd-flex-col cd-gap-2">
                      <Text className="cd-text-sm cd-font-semibold cd-text-gray-800 dark:cd-text-gray-200">
                        {event.resource.name}
                      </Text>
                      <Text className="cd-text-xs">
                        {moment(event.start).format(DATE_FORMAT)} -{' '}
                        {moment(event.end).format(DATE_FORMAT)}
                      </Text>
                      <Divider className="cd-my-2" />
                      <TextIcon
                        className="cd-text-xs cd-gap-[0.25rem]"
                        leftIcon={<IconUser size={14} />}
                      >
                        {event.resource.client?.name || 'Sin cliente'}
                      </TextIcon>
                      <TextIcon
                        className="cd-text-xs cd-gap-[0.25rem]"
                        leftIcon={<IconPhoneCall size={14} />}
                      >
                        {event.resource.contacts?.length
                          ? `${event.resource.contacts.length} contacto(s)`
                          : 'Sin contacto'}
                      </TextIcon>
                      <div className="cd-flex cd-flex-row cd-items-center cd-justify-end cd-gap-2 cd-mt-1 cd-flex-wrap">
                        <Badge color={event.resource.type?.color || 'gray'}>
                          {event.resource.type?.name || 'Sin tipo'}
                        </Badge>
                        <Badge color={event.resource.status?.color || 'gray'}>
                          {event.resource.status?.name || 'Sin estado'}
                        </Badge>
                      </div>
                    </Box>
                  </HoverCard.Dropdown>
                </HoverCard>
              ),
              // eventWrapper: ({ event }) => (
              //   <Paper className="!cd-bg-white dark:!cd-bg-zinc-300" shadow="md">
              //     <Box
              //       bg={transformColor({
              //         color: event.resource.type?.color || '#ccc',
              //         shift: 0.75,
              //         alpha: 1,
              //       })}
              //       className="cd-p-2 cd-cursor-pointer"
              //       style={{
              //         borderWidth: 1,
              //         borderRadius: 5,
              //         borderColor: event.resource.type?.color || 'gray',
              //       }}
              //       onClick={(e) => {
              //         e.stopPropagation()
              //         setEventPreview({ open: true, data: { event: event.resource } })
              //       }}
              //     >
              //       <Text className="cd-text-sm cd-font-semibold cd-text-gray-800 dark:cd-text-gray-200">
              //         {event.title}
              //       </Text>
              //       <Text className="cd-text-xs">
              //         {moment(event.start).format(DATE_FORMAT)} -{' '}
              //         {moment(event.end).format(DATE_FORMAT)}
              //       </Text>
              //       <Divider
              //         className="cd-my-2"
              //         color={transformColor({
              //           color: event.resource.type?.color || '#ccc',
              //           shift: 0.25,
              //           alpha: 0.75,
              //         })}
              //       />

              //       <TextIcon
              //         className="cd-text-xs cd-gap-[0.25rem]"
              //         leftIcon={<IconUser size={14} />}
              //       >
              //         {event.resource.client?.name || 'Sin cliente'}
              //       </TextIcon>
              //       <TextIcon
              //         className="cd-text-xs cd-gap-[0.25rem]"
              //         leftIcon={<IconPhoneCall size={14} />}
              //       >
              //         {event.resource.contacts?.length
              //           ? `${event.resource.contacts.length} contactos(s)`
              //           : 'Sin contacto'}
              //       </TextIcon>
              //       <div className="cd-flex cd-flex-row cd-items-center cd-justify-end cd-gap-2 cd-mt-1">
              //         <Badge color={event.resource.type?.color || 'gray'}>
              //           {event.resource.type?.name || 'Sin tipo'}
              //         </Badge>
              //         <Badge color={event.resource.status?.color || 'gray'}>
              //           {event.resource.status?.name || 'Sin estado'}
              //         </Badge>
              //       </div>
              //       {/* <div className="cd-flex cd-flex-row cd-items-center cd-justify-end cd-gap-2 cd-mt-1">
              //         <ActionIcon
              //           color="red"
              //           variant="subtle"
              //           onClick={(e) => {
              //             e.stopPropagation()
              //             setEventModalDelete([event.resource])
              //           }}
              //         >
              //           <IconTrash size={20} />
              //         </ActionIcon>
              //       </div> */}
              //     </Box>
              //   </Paper>
              // ),
            }}
            defaultDate={moment().toDate()}
            endAccessor={(event) => new Date(event.end)}
            events={calendarParse.data || []}
            localizer={localizer}
            startAccessor={(event) => new Date(event.start)}
            step={60}
          />
        </React.Fragment>
      )}

      <EventPreview
        modal
        data={modalEventPreview.data}
        open={modalEventPreview.open}
        size="lg"
        onClose={() => setEventPreview({ open: false })}
        onEdit={() => {
          setEventForm({
            open: true,
            data: { event: modalEventPreview.data?.event } as EventFormType,
          })
          setEventPreview({ open: false })
        }}
      />

      <EventForm
        modal
        data={modalEventForm.data}
        open={modalEventForm.open}
        size="lg"
        onClose={() => setEventForm({ open: false })}
      />

      <ImagePreview
        modal
        data={modalImagePreview.data}
        open={modalImagePreview.open}
        size="xl"
        onClose={() => setModalImagePreview({ open: false })}
      />

      <ConfirmationModal
        cancelColor="gray"
        cancelText="Cancelar"
        confirmColor="red"
        confirmText="Eliminar"
        loading={isLoadingMutation(deleteEventsMutation)}
        open={Boolean(eventModalDelete?.length)}
        title="Eliminar evento"
        onCancel={() => setEventModalDelete(null)}
        onConfirm={() => {
          if (!eventModalDelete && selectedRows.length === 0) return

          const selectedEvents =
            eventModalDelete ??
            eventsAllQuery.data?.filter((item) => selectedRows.includes(item.uid))

          deleteEventsMutation.mutate(selectedEvents?.map((item) => item.uid) || [])
        }}
      >
        {eventModalDelete && eventModalDelete.length === 1 && (
          <Text className="cd-text-base">
            ¿Estás seguro de eliminar el evento <strong>{eventModalDelete[0].name}</strong>?
          </Text>
        )}
        {eventModalDelete && eventModalDelete.length > 1 && (
          <Text className="cd-text-base">
            ¿Estás seguro de eliminar los <strong>{eventModalDelete.length} eventos</strong>?
          </Text>
        )}
      </ConfirmationModal>
    </div>
  )

  function buildColumns() {
    return [
      {
        key: 'status',
        label: 'Estado',
        width: '10%',
        type: 'calc',
        align: 'center',
        searchable: true,
        render: ({ status }) => {
          return (
            <Badge color={status?.color || 'gray'} size="md">
              {status?.name || 'Sin estado'}
            </Badge>
          )
        },
      },
      {
        key: 'type',
        label: 'Tipo',
        width: '10%',
        type: 'calc',
        align: 'center',
        searchable: true,
        render: ({ type }) => {
          return (
            <Badge color={type?.color || 'gray'} size="md">
              {type?.name || 'Sin tipo'}
            </Badge>
          )
        },
      },
      { key: 'name', label: 'Nombre', width: '20%', type: 'text', searchable: true },
      {
        key: 'description',
        label: 'Descripción',
        width: '30%',
        type: 'calc',
        searchable: true,

        render: ({ description }) => (
          <Tooltip withArrow label={description || 'Sin descripción'} position="top">
            <Text className="cd-line-clamp-2" lineClamp={2}>
              {description || 'Sin descripción'}
            </Text>
          </Tooltip>
        ),
      },
      {
        key: 'attachments',
        label: 'Imágenes',
        width: '7%',
        type: 'calc',
        render: ({ attachments }) =>
          attachments.length ? (
            <Group justify="center">
              <ActionIcon
                variant="transparent"
                onClick={() => {
                  setModalImagePreview({ open: true, data: attachments })
                }}
              >
                <IconPaperclip size={18} />
              </ActionIcon>
            </Group>
          ) : (
            <Box c="dimmed">Sin imagen</Box>
          ),
      },
      {
        key: 'date',
        label: 'Fecha',
        width: '15%',
        type: 'calc',
        searchable: true,
        render: ({ date }) => moment.unix(date).format(DATE_FORMAT),
      },
      {
        key: 'client',
        label: 'Cliente',
        width: '7%',
        type: 'calc',
        align: 'right',
        render: ({ client }) => <Text>{client?.name || 'Sin cliente'}</Text>,
      },
      {
        key: 'actions',
        label: 'Acciones',
        width: '8%',
        type: 'calc',
        render: (item) => (
          <Group justify="center">
            <ActionIcon
              variant="transparent"
              onClick={() => {
                setEventForm({
                  open: true,
                  data: {
                    clientID: item.client.uid,
                    event: item,
                  },
                })
              }}
            >
              <IconEdit size={18} />
            </ActionIcon>
            <ActionIcon
              color="red"
              variant="transparent"
              onClick={() => {
                setEventModalDelete([item])
              }}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        ),
      },
    ] as GenericColumnType<EventBaseType>[]
  }

  function handleAddStatus(evt: any) {
    const fromGroupUid = evt.from.getAttribute('id') as string | null
    const toGroupUid = evt.to.getAttribute('id') as string | null
    const itemId = evt.item.dataset.id as string | null

    if (!fromGroupUid || !toGroupUid || !itemId) {
      console.error('Missing group or item information')
      return
    }

    setGroupedDataByStatus((prevGroups) => {
      const sourceGroup = prevGroups.find((g) => g.uid === fromGroupUid)
      const targetGroup = prevGroups.find((g) => g.uid === toGroupUid)

      if (!sourceGroup || !targetGroup) return prevGroups

      const itemIndex = sourceGroup.events.findIndex((c) => c.uid === itemId)

      if (itemIndex === -1) return prevGroups

      const [movedItem] = sourceGroup.events.splice(itemIndex, 1)

      targetGroup.events.splice(evt.newIndex, 0, movedItem)

      const statusToUpdate = prevGroups.find((g) => g.uid === toGroupUid)

      if (!statusToUpdate) {
        console.error('Status not found for group:', toGroupUid)
        return [...prevGroups]
      }

      debounceUpdate<UpdateEventType>({
        delay: 0,
        updateFn: (data) => eventPropertyMutation.mutate(data),
        callId: itemId,
        dataFunction: {
          clientID: movedItem.client?.uid || '',
          data: {
            uid: movedItem.uid,
            status: {
              uid: statusToUpdate.uid,
              name: statusToUpdate.name,
              color: statusToUpdate.color,
              createdAt: statusToUpdate.createdAt,
              updatedAt: statusToUpdate.updatedAt,
              order: statusToUpdate.order,
            },
          },
        },
      })

      // const newGroups = prevGroups.map((group) => {
      //   if (group.uid === fromGroupUid) {
      //     const newEvents = sourceGroup.events.filter((e) => e.uid !== itemId)
      //     return { ...group, events: newEvents }
      //   }
      //   if (group.uid === toGroupUid) {
      //     const newEvents = [...targetGroup.events]
      //     newEvents.splice(evt.newIndex, 0, movedItem)
      //     return { ...group, events: newEvents }
      //   }
      //   return group
      // })

      return [...prevGroups]
    })
  }

  function handleAddType(evt: any) {
    const fromGroupUid = evt.from.getAttribute('id') as string | null
    const toGroupUid = evt.to.getAttribute('id') as string | null
    const itemId = evt.item.dataset.id as string | null

    if (!fromGroupUid || !toGroupUid || !itemId) {
      console.error('Missing group or item information')
      return
    }

    setGroupedDataByType((prevGroups) => {
      const sourceGroup = prevGroups.find((g) => g.uid === fromGroupUid)
      const targetGroup = prevGroups.find((g) => g.uid === toGroupUid)

      if (!sourceGroup || !targetGroup) return prevGroups

      const itemIndex = sourceGroup.events.findIndex((c) => c.uid === itemId)

      if (itemIndex === -1) return prevGroups

      const [movedItem] = sourceGroup.events.splice(itemIndex, 1)

      targetGroup.events.splice(evt.newIndex, 0, movedItem)

      const typeToUpdate = prevGroups.find((g) => g.uid === toGroupUid)

      if (!typeToUpdate) {
        console.error('Type not found for group:', toGroupUid)
        return [...prevGroups]
      }

      debounceUpdate<UpdateEventType>({
        delay: 0,
        updateFn: (data) => eventPropertyMutation.mutate(data),
        callId: itemId,
        dataFunction: {
          clientID: movedItem.client?.uid || '',
          data: {
            uid: movedItem.uid,
            type: {
              uid: typeToUpdate.uid,
              name: typeToUpdate.name,
              color: typeToUpdate.color,
              createdAt: typeToUpdate.createdAt,
              updatedAt: typeToUpdate.updatedAt,
            },
          },
        },
      })

      return [...prevGroups]
    })
  }
}

export default HomeUser
